'use client'

import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useAdmin } from '@/hooks/useAdmin'
import { getEntitlements, isSelfLearnActive } from '@/utils/entitlements'
import { PlanBadge } from '@/components/subscription/PlanBadge'
import { getDashboardPath } from '@/utils/getDashboardPath'
import { SageLearnerInsight } from '@/components/education/SageLearnerInsight'
import { RadialProgress } from '@/components/education/RadialProgress'
import {
  Users, BookOpen, Pencil, Target, Award, FileText, CreditCard,
  GraduationCap, Plus, ChevronRight, ChevronDown, Loader2, X,
  Sparkles, TrendingUp, TrendingDown, Minus, Eye, EyeOff, Star,
  Compass, ArrowRight, CheckCircle2, KeyRound, Mail
} from 'lucide-react'

import AddSelfLearnModal from '@/components/homeschool/AddSelfLearnModal'
import CertificateGallery from '@/components/education/CertificateGallery'
import CredentialRecordsPanel from '@/components/education/CredentialRecordsPanel'
import { CommunityLibraryModal, cloneCommunityCourse } from '@/components/education/CommunityLibraryBrowser'
import { QRCodeSVG } from 'qrcode.react'
import SEO from '@/components/layout/SEO'

// ── Design tokens — DARK family, matched to ChildSubjectsPage (the reference
// surface). Self-contained: immune to global CSS token state.
// System: BLUE = actions/primary · GREEN = success · AMBER = attention ·
// ROSE = struggle · GOLD = brand/demo accents only.
const INK = '#F2F5FA'
const MUTED = '#9AA7BC'
const BORDER = 'rgba(255,255,255,0.10)'
const BLUE = '#0EA5E9'
const GOLD = '#E8C268'
const GOLD_TINT = 'rgba(232,194,104,0.10)'
const GOLD_BORDER = 'rgba(232,194,104,0.30)'
const SAGE = BLUE        // action colour = electric blue (ChildSubjects primary family)
const SAGE_TINT = 'rgba(14,165,233,0.12)'
const GREEN = '#4ADE80'
const GREEN_TINT = 'rgba(74,222,128,0.12)'
const AMBER = '#FBBF24'
const AMBER_TINT = 'rgba(251,191,36,0.12)'
const ROSE = '#FB7185'
const ROSE_TINT = 'rgba(251,113,133,0.12)'
const CANVAS = '#0A0F1E'
const CARD = '#0F1930'
const DEMO_CHILD_ID = '00000000-0000-4000-a000-000000000a4a'

const masteryTone = (s: number) =>
  s >= 80 ? { fg: GREEN, bg: GREEN_TINT }
  : s >= 60 ? { fg: AMBER, bg: AMBER_TINT }
  : { fg: ROSE, bg: ROSE_TINT }

const fmtDate = (d?: string | null) => {
  if (!d) return '—'
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 14) return `${days} days ago`
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

const fmtFull = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

const initials = (n: string) => n.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()

// ── Types ────────────────────────────────────────────────────────────────────
interface ReportRow {
  unit_number: number
  lesson_number: number
  completed_at: string
  class_name?: string
  phase?: string
  attempt?: number
  concept_understood?: string
  weak_concept_flagged?: string
  score?: number
  passed?: boolean
  strong_areas?: string[]
  weak_areas?: string[]
}

export interface ChildEnrollment {
  class_id: string
  class_name: string
  category_id?: string
  mastery_score: number
  last_active_at: string | null
  total_lessons: number
  progress_state: {
    current_unit: number
    current_lesson: number
    total_lessons_completed: number
  } | null
}

interface ChildProgress {
  child_id: string
  name: string
  child_email?: string
  age: number
  is_demo?: boolean
  global_academic_profile?: any
  recentLogs: ReportRow[]
  enrollments: ChildEnrollment[]
  totalLessons: number
  avgMastery: number
  masteryTrend: number | null       // Δ vs 2 weeks ago, from quiz history (null = not enough data)
  strengths: string[]
  weaknesses: string[]
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ParentDashboardPage() {
  const { user, userProfile, isLoading: authLoading, updateProfile } = useAuth()
  const { isAdmin } = useAdmin()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [children, setChildren] = useState<ChildProgress[]>([])
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [generatingCertFor, setGeneratingCertFor] = useState<string | null>(null)
  const [showSelfLearnModal, setShowSelfLearnModal] = useState(false)
  const [showDemoTile, setShowDemoTile] = useState(true)
  const [demoOpen, setDemoOpen] = useState(false)
  const [searchParams] = useSearchParams()
  const [childCredentials, setChildCredentials] = useState<{ name: string; email: string; password: string }[] | null>(null)
  const [resettingChildId, setResettingChildId] = useState<string | null>(null)
  const [resetSuccessCreds, setResetSuccessCreds] = useState<{ name: string; email: string; password: string } | null>(null)
  const [magicLinkInfo, setMagicLinkInfo] = useState<{ childName: string; link: string; emailedTo: string } | null>(null)
  const [upgradeEmailFor, setUpgradeEmailFor] = useState<{ childId: string; childName: string } | null>(null)
  const [upgradeEmailValue, setUpgradeEmailValue] = useState('')
  const [upgradeLoading, setUpgradeLoading] = useState(false)
  // ── Community library door state ──
    const [communityOpen, setCommunityOpen] = useState(false)
  // ── Subscriber affiliate banner state ──
  const [showAffiliateBanner, setShowAffiliateBanner] = useState(true)

  const handleResetChildPassword = async (childId: string, childName: string, mode: 'magic_link' | 'password' = 'password') => {
    if (mode === 'magic_link') {
      if (!confirm(`Email ${childName}'s sign-in link to your email? ${childName} opens it on their device and lands straight in their learning.`)) return
    } else {
      if (!confirm(`Reset password for ${childName}? A new word password will be generated.`)) return
    }
    setResettingChildId(childId)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/reset-child-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ child_id: childId, mode }),
      })
      const data = await res.json()
      if (!res.ok || data.error) throw new Error(data.error || 'Failed')

      if (mode === 'magic_link') {
        setMagicLinkInfo({
          childName: data.child_name || childName,
          link: data.magic_link,
          emailedTo: user?.email || 'your email',
        })
      } else {
        setResetSuccessCreds({
          name: data.child_name || childName,
          email: data.email,
          password: data.password,
        })
      }
    } catch (err: any) {
      alert(`Could not ${mode === 'magic_link' ? 'send sign-in link' : 'reset password'}: ${err.message}`)
    } finally {
      setResettingChildId(null)
    }
  }

  const handleUpgradeChildEmail = (childId: string, childName: string) => {
    setUpgradeEmailFor({ childId, childName })
    setUpgradeEmailValue('')
  }

  const submitUpgradeEmail = async () => {
    if (!upgradeEmailFor || !upgradeEmailValue.trim()) return
    setUpgradeLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')
      const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/update-child-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ child_id: upgradeEmailFor.childId, new_email: upgradeEmailValue.trim() }),
      })
      const data = await res.json()
      if (!res.ok || data.error) throw new Error(data.error || 'Failed')
      alert(data.message)
      const targetId = upgradeEmailFor.childId
      const newEmail = upgradeEmailValue.trim()
      setUpgradeEmailFor(null)
      setUpgradeEmailValue('')
      setChildren(prev => prev.map(c => c.child_id === targetId ? { ...c, child_email: newEmail } : c))
    } catch (err: any) {
      alert(err.message)
    } finally {
      setUpgradeLoading(false)
    }
  }

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      try {
        const raw = sessionStorage.getItem('sw_child_credentials')
        if (raw) {
          setChildCredentials(JSON.parse(raw))
          sessionStorage.removeItem('sw_child_credentials')
        }
      } catch { /* non-fatal */ }
    }
  }, [searchParams])


  // Facility guard — non-homeschool admins have their own portals
  useEffect(() => {
    if (!authLoading && !user) navigate('/signin', { replace: true })
    if (!authLoading && !isAdmin && userProfile?.facility_id && userProfile?.facility_type) {
      if (!['homeschool', 'family_edu', 'family'].includes(userProfile.facility_type)) {
        if (userProfile.facility_type === 'church') { navigate('/church/admin', { replace: true }); return }
        if (userProfile.facility_type === 'facility') { navigate('/facility-admin', { replace: true }); return }
        if (['ngo', 'university_ngo'].includes(userProfile.facility_type)) { navigate('/ngo/dashboard', { replace: true }); return }
        navigate(getDashboardPath(userProfile), { replace: true })
      }
    }
  }, [user, userProfile, authLoading, isAdmin, navigate])

  // ── Data load: real children (facility-scoped) + THE shared demo student ──
  useEffect(() => {
    if (!user) return
    let cancelled = false

    async function loadChildren() {
      setLoading(true)
      try {
        const { data: parentUser } = await supabase
          .from('users').select('facility_id').eq('id', user!.id).single()
        if (cancelled) return

        const list: ChildProgress[] = []

        // ── 1. Real children ──
        if (parentUser?.facility_id) {
          const { data: childUsers } = await supabase
            .from('users')
            .select('id, name, email, age, age_bracket, global_academic_profile')
            .eq('facility_id', parentUser.facility_id)
            .eq('facility_role', 'member')
          if (cancelled) return

          const childIds = (childUsers || []).map(c => c.id)

          const [enrRes, logRes, quizRes] = await Promise.all([
            childIds.length
              ? supabase.from('class_enrollments')
                  .select('student_id, class_id, mastery_score, last_active_at, progress_state, classes (name, category_id, lesson_count)')
                  .in('student_id', childIds)
              : Promise.resolve({ data: [] } as any),
            childIds.length
              ? supabase.from('student_lesson_logs')
                  .select('user_id, unit_number, lesson_number, completed_at, metadata')
                  .in('user_id', childIds)
                  .order('completed_at', { ascending: false })
              : Promise.resolve({ data: [] } as any),
            childIds.length
              ? supabase.from('education_quiz_results')
                  .select('user_id, score_percentage, attempted_at')
                  .in('user_id', childIds)
                  .order('attempted_at', { ascending: false })
              : Promise.resolve({ data: [] } as any),
          ])
          if (cancelled) return

          for (const child of childUsers || []) {
            const childEnrol: ChildEnrollment[] = (enrRes.data || [])
              .filter((e: any) => e.student_id === child.id)
              .map((e: any) => ({
                class_id: e.class_id,
                class_name: (e.classes as any)?.name || 'Unknown',
                category_id: (e.classes as any)?.category_id,
                mastery_score: Number(e.mastery_score) || 0,
                last_active_at: e.last_active_at,
                total_lessons: Number((e.classes as any)?.lesson_count) || 0,
                progress_state: (e.progress_state as any) ?? { current_unit: 1, current_lesson: 1, total_lessons_completed: 0 },
              }))

            const childLogsRaw = (logRes.data || []).filter((l: any) => l.user_id === child.id)
            const strengthsSet = new Set<string>()
            const weaknessesSet = new Set<string>()
            for (const log of childLogsRaw) {
              const m = (log.metadata as Record<string, any>) || {}
              if (Array.isArray(m.strong_areas)) m.strong_areas.forEach((s: string) => strengthsSet.add(s))
              if (Array.isArray(m.weak_areas)) m.weak_areas.forEach((w: string) => weaknessesSet.add(w))
              if (typeof m.weak_concept_flagged === 'string') weaknessesSet.add(m.weak_concept_flagged)
            }

            // Trend: average quiz score in the last 14 days vs the 14 before that
            const childQuizzes = (quizRes.data || []).filter((q: any) => q.user_id === child.id)
            const now = Date.now()
            const recent = childQuizzes.filter((q: any) => now - new Date(q.attempted_at).getTime() < 14 * 86400000)
            const older = childQuizzes.filter((q: any) => {
              const age = now - new Date(q.attempted_at).getTime()
              return age >= 14 * 86400000 && age < 28 * 86400000
            })
            let masteryTrend: number | null = null
            if (recent.length && older.length) {
              const avg = (arr: any[]) => arr.reduce((s: number, q: any) => s + (q.score_percentage || 0), 0) / arr.length
              masteryTrend = Math.round(avg(recent) - avg(older))
            } else if (recent.length >= 2 && !older.length) {
              masteryTrend = 0 // stabilising — not enough history for a trend yet
            }

            list.push({
              child_id: child.id,
              name: child.name || 'Child',
              child_email: child.email,
              age: child.age || 0,
              global_academic_profile: child.global_academic_profile || null,
              recentLogs: childLogsRaw.slice(0, 6).map((l: any) => {
                const m = l.metadata || {}
                return {
                  unit_number: l.unit_number, lesson_number: l.lesson_number, completed_at: l.completed_at,
                  class_name: m.class_name, phase: m.phase, attempt: m.attempt,
                  concept_understood: m.concept_understood, weak_concept_flagged: m.weak_concept_flagged,
                  score: m.score, passed: m.passed,
                  strong_areas: m.strong_areas || [], weak_areas: m.weak_areas || [],
                }
              }),
              enrollments: childEnrol,
              totalLessons: childEnrol.reduce((s: number, e: ChildEnrollment) => s + (e.progress_state?.total_lessons_completed || 0), 0),
              avgMastery: childEnrol.length
                ? Math.round(childEnrol.reduce((s: number, e: ChildEnrollment) => s + e.mastery_score, 0) / childEnrol.length) : 0,
              masteryTrend,
              strengths: Array.from(strengthsSet).slice(0, 8),
              weaknesses: Array.from(weaknessesSet).slice(0, 8),
            })
          }
        }

        // ── 2. THE demo student — same shape, same components (flag-gated) ──
        const hideDemo = !!((userProfile as any)?.hide_demo_child)
        if (!hideDemo) {
          const { data: show, error: showcaseErr } = await supabase
            .from('demo_child_showcase')
            .select('*')
            .maybeSingle()
          if (showcaseErr) {
            console.warn('[ParentDashboard] demo_child_showcase query error:', showcaseErr)
          }
          if (cancelled) return
          if (show) {
            const rawEnrollments = (Array.isArray(show.enrollments) ? show.enrollments : []) as Array<Record<string, any>>
            const enrol = rawEnrollments[0]
            const ps = (enrol?.progress_state as { current_unit?: number; current_lesson?: number; total_lessons_completed?: number } | undefined) ?? { current_unit: 2, current_lesson: 3, total_lessons_completed: 5 }
            const rawLogs = (Array.isArray(show.recent_logs) ? show.recent_logs : []) as Array<Record<string, any>>
            const dLogs: ReportRow[] = rawLogs.map((l) => {
              const m = (l.metadata as Record<string, any>) || {}
              return {
                unit_number: l.unit_number, lesson_number: l.lesson_number, completed_at: l.completed_at,
                class_name: m.class_name, phase: m.phase, attempt: m.attempt,
                concept_understood: m.concept_understood, weak_concept_flagged: m.weak_concept_flagged,
                score: m.score, passed: m.passed,
                strong_areas: m.strong_areas || [], weak_areas: m.weak_areas || [],
              }
            })
            // Demo trend: last 2 vs prior 2 quizzes → +6 (80 after the 60-fail recovery)
            const quizLogs = dLogs.filter(l => l.phase === 'quiz_result')
            let demoTrend: number | null = null
            if (quizLogs.length >= 4) {
              const avg = (arr: ReportRow[]) => arr.reduce((s: number, l: ReportRow) => s + (l.score || 0), 0) / arr.length
              demoTrend = Math.round(avg(quizLogs.slice(0, 2)) - avg(quizLogs.slice(2, 4)))
            }
            const profile = show.global_academic_profile as { strengths?: string[]; struggles?: string[]; [key: string]: any } | null
            list.push({
              child_id: DEMO_CHILD_ID,
              name: show.child_name || 'Ava',
              age: show.child_age ?? 12,
              is_demo: true,
              global_academic_profile: profile,
              recentLogs: dLogs,
              enrollments: rawEnrollments.map(e => ({
                class_id: e.class_id || '',
                class_name: e.class_name || 'Subject',
                category_id: e.category_id,
                mastery_score: Number(e.mastery_score) || 0,
                last_active_at: e.last_active_at || null,
                total_lessons: Array.isArray(e.module_plan)
                  ? (e.module_plan as any[]).reduce((s: number, m: any) => s + (m.lessons?.length || 0), 0)
                  : 0,
                progress_state: e.progress_state || null,
              })),
              totalLessons: ps.total_lessons_completed || 0,
              avgMastery: Math.round(Number(enrol?.mastery_score) || 0),
              masteryTrend: demoTrend,
              strengths: profile?.strengths || [],
              weaknesses: profile?.struggles || [],
            })
          }
        }

        if (cancelled) return
        setChildren(list)
        setSelectedChildId(prev =>
          prev && list.some(c => c.child_id === prev) ? prev : (list[0]?.child_id ?? null)
        )
      } catch (err) {
        console.error('Error loading children:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadChildren()
    return () => { cancelled = true }
  }, [user, (userProfile as any)?.hide_demo_child])


  const activeChild = children.find(c => c.child_id === selectedChildId) || null
  const { isPaid, plan: familyPlan } = getEntitlements(userProfile)

  function masteryToneFn(s: number) {
    return s >= 80 ? { fg: GREEN, bg: GREEN_TINT } : s >= 60 ? { fg: AMBER, bg: AMBER_TINT } : { fg: ROSE, bg: ROSE_TINT }
  }

  // ── Certificate download (real children only) ──
  const handleDownloadCertificate = async (child: ChildProgress, enrol: any) => {
    setGeneratingCertFor(enrol.class_id)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) return
      const res = await fetch(`${(supabase as any).supabaseUrl}/functions/v1/generate-certificate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          type: 'sage',
          category_id: enrol.category_id,
          class_id: enrol.class_id,
          student_id: child.child_id,
        }),
      })
      const json = await res.json()
      if (json.certificate_url) {
        const a = document.createElement('a')
        a.href = json.certificate_url
        a.download = `${child.name}-${enrol.class_name}-Certificate.pdf`
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else {
        alert(json.error || 'Could not generate certificate.')
      }
    } catch (e: any) {
      alert(e.message || 'Error generating certificate.')
    } finally {
      setGeneratingCertFor(null)
    }
  }

  // ── Demo dismissal ──
  const handleHideDemo = async () => {
    setShowDemoTile(false)
    if (selectedChildId === DEMO_CHILD_ID) setSelectedChildId(children.find(c => !c.is_demo)?.child_id ?? null)
    setChildren(prev => prev.filter(c => !c.is_demo))
    try {
      await updateProfile({
        hide_demo_child: true,
      } as any)
    } catch { /* non-fatal — UI already updated */ }
  }


  const TrendChip = ({ trend }: { trend: number | null }) => {
    if (trend === null || trend === 0) return (
      <span className="inline-flex items-center gap-0.5 text-[10.5px] font-bold px-1.5 py-0.5 rounded"
        style={{ background: '#F1EEE7', color: MUTED }}>
        <Minus size={9} />
      </span>
    )
    const up = trend > 0
    return (
      <span className="inline-flex items-center gap-0.5 text-[10.5px] font-bold px-1.5 py-0.5 rounded"
        style={{ background: up ? GREEN_TINT : ROSE_TINT, color: up ? GREEN : ROSE }}>
        {up ? <TrendingUp size={9} /> : <TrendingDown size={9} />} {up ? '+' : ''}{trend}%
      </span>
    )
  }

  // ── Per-child summary card (hero pattern from class-leading portals) ──
  const ChildHero = ({ child }: { child: ChildProgress }) => {
    return (
      <div className="rounded-2xl p-5 sm:p-6 mb-5 shadow-sm"
        style={{ background: 'linear-gradient(135deg, #141B2E 0%, #1B2440 100%)' }}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black text-white flex-shrink-0"
            style={child.is_demo
              ? { background: 'linear-gradient(135deg,#C9A84C,#A8842C)' }
              : { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
            {initials(child.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-white truncate">{child.name}</h2>
              {child.is_demo && (
                <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full border"
                  style={{ background: GOLD_TINT, borderColor: GOLD_BORDER, color: GOLD }}>
                  <Sparkles size={9} /> Sample Student
                </span>
              )}
            </div>
            <p className="text-[12.5px] text-white/50 mt-0.5">
              {child.age ? `Age ${child.age}` : ''}
              {child.enrollments.length ? ` · ${child.enrollments.length} ${child.enrollments.length === 1 ? 'course' : 'courses'}` : ''}
            </p>
            {!child.is_demo && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  onClick={() => handleResetChildPassword(child.child_id, child.name, 'magic_link')}
                  disabled={resettingChildId === child.child_id}
                  className="text-[11px] font-bold inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-emerald-300/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 transition-colors"
                >
                  {resettingChildId === child.child_id ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Sparkles size={11} />
                  )}
                  <span>Email {child.name}'s sign-in link</span>
                </button>
                <button
                  onClick={() => handleResetChildPassword(child.child_id, child.name, 'password')}
                  disabled={resettingChildId === child.child_id}
                  className="text-[11px] font-bold inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <KeyRound size={11} />
                  <span>{t('homeschool.show_password_instead')}</span>
                </button>
                {!child.is_demo && child.child_id && (child as any).child_email?.endsWith('@stillwaters.internal') && (
                  <button
                    onClick={() => handleUpgradeChildEmail(child.child_id, child.name)}
                    className="text-[11px] font-semibold inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-blue-300/30 bg-blue-500/5 hover:bg-blue-500/15 text-blue-300 transition-colors"
                    title="For older children with their own email address, they manage their own sign-in from then on"
                  >
                    <Mail size={11} />
                    <span>{t('homeschool.use_their_own_email')}</span>
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex-shrink-0 hidden sm:block">
            <RadialProgress score={child.avgMastery} size={76} strokeWidth={7}
              accentColor={child.is_demo ? '#E8D9AE' : 'rgba(255,255,255,0.9)'}
              trackColor="rgba(255,255,255,0.15)"
              label={t('homeschool.child.stats.mastery', 'Mastery')} />
          </div>
        </div>
        {/* Mobile: ring inline under name */}
        <div className="sm:hidden flex items-center justify-between mt-4 pt-4 border-t border-white/10">
          <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">
            {t('homeschool.child.stats.mastery', 'Mastery')}
          </span>
          <div className="flex items-center gap-2.5">
            <TrendChip trend={child.masteryTrend} />
            <span className="text-xl font-black text-white tabular-nums">{child.avgMastery}%</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ background: CANVAS }}>
      <SEO title="Parent Dashboard" noindex />

      {/* ── Header band ── */}
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0A0D17 0%, #141B2E 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-7 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-[10px] font-black tracking-[0.2em] uppercase mb-1" style={{ color: '#D4A843' }}>
              Still Waters Academy
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {t('homeschool.parent.title', 'Parent Portal')}
            </h1>
            <p className="text-white/50 text-[13px] mt-1">
              {t('homeschool.parent.subtitle', 'Live academic progress, AI teacher insights, and curriculum management.')}
            </p>
          </div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <PlanBadge userProfile={userProfile} />
            {userProfile?.facility_id && (
              <button onClick={() => navigate('/learn')}
                className="px-3.5 py-2 rounded-xl text-xs font-bold border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center gap-1.5 transition-colors"
                title="Browse your own learning dashboard">
                <BookOpen size={12} />
                <span>{t('homeschool.parent.myLearning', 'My Learning')}</span>
              </button>
            )}
            {userProfile?.facility_id && (
              <button onClick={() => setCommunityOpen(true)}
                className="px-3.5 py-2 rounded-xl text-xs font-bold border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center gap-1.5 transition-colors"
                title="Browse free teacher-built electives">
                <span>🌍</span>
                <span>{t('homeschool.community_library')}</span>
              </button>
            )}
            {children.filter(c => !c.is_demo).length > 0 && (
              <button onClick={() => navigate(`/record/${children.find(c => !c.is_demo)!.child_id}`)}
                className="px-3.5 py-2 rounded-xl text-xs font-bold border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center gap-1.5 transition-colors"
                title="Download learning record for your child">
                <FileText size={12} />
                <span>{t('homeschool.parent.myTranscript', 'My Transcript')}</span>
              </button>
            )}
            <button onClick={() => navigate('/how-it-works')}
              className="px-3.5 py-2 rounded-xl text-xs font-bold border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center gap-1.5 transition-colors">
              <BookOpen size={12} />
              <span>{t('homeschool.how_it_works')}</span>
            </button>
            <button onClick={() => navigate('/prep')}
              className="px-3.5 py-2 rounded-xl text-xs font-bold border border-white/20 bg-white/10 hover:bg-white/20 text-white flex items-center gap-1.5 transition-colors">
              <Sparkles size={12} className="text-[#D4A843]" />
              <span>{t('homeschool.parent.trialPrep', 'Trial Prep Lessons')}</span>
            </button>
            <button onClick={() => navigate(isPaid ? '/homeschool/subscription' : '/homeschool/enrol?mode=child')}
              className="px-4 py-2 rounded-xl text-xs font-bold text-white transition-colors"
              style={{ background: BLUE }}>
              {isPaid ? t('homeschool.parent.managePlan', 'Manage Plan') : t('homeschool.parent.enrolNow', 'Enrol Now')}
            </button>
          </div>
        </div>
      </div>

      {/* ── Top Alert Banners ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 space-y-3">
        {upgradeEmailFor && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setUpgradeEmailFor(null)}>
            <div className="rounded-2xl p-6 max-w-md w-full shadow-2xl border"
              style={{ background: CARD, borderColor: BORDER }}
              onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-black mb-2" style={{ color: INK }}>
                {upgradeEmailFor.childName}'s own email
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: MUTED }}>
                For older children who manage their own sign-in. We'll send a confirmation
                to this address: {upgradeEmailFor.childName} clicks it to take ownership.
                After that, their sign-in links go to their inbox, not yours.
              </p>
              <input
                type="email"
                value={upgradeEmailValue}
                onChange={e => setUpgradeEmailValue(e.target.value)}
                placeholder="bobby@example.com"
                className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none focus:border-emerald-400 mb-4" style={{ background: CANVAS, borderColor: BORDER, color: INK }}
              />
              <div className="flex gap-2">
                <button onClick={() => setUpgradeEmailFor(null)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-500">
                  Cancel
                </button>
                <button onClick={submitUpgradeEmail}
                  disabled={upgradeLoading || !upgradeEmailValue.trim()}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold">
                  {upgradeLoading ? 'Sending…' : 'Send confirmation'}
                </button>
              </div>
            </div>
          </div>
        )}

        {magicLinkInfo && (
          <div className="rounded-2xl border-2 p-4 sm:p-5 shadow-sm"
            style={{ background: GREEN_TINT, borderColor: 'rgba(74,222,128,0.35)' }}>
            <div className="font-extrabold text-[15px] mb-1" style={{ color: GREEN }}>
              ✓ {magicLinkInfo.childName}'s sign-in link is on its way
            </div>
            <p className="text-[12.5px] mb-3" style={{ color: '#BBF7D0' }}>
              Emailed to <strong>{magicLinkInfo.emailedTo}</strong>. Open it on {magicLinkInfo.childName}'s
              device and tap the button, they land straight in their learning. No email, no password to type.
            </p>
            <div className="flex items-start gap-4">
              <div className="bg-white rounded-xl p-3 border shrink-0" style={{ borderColor: '#BBF7D0' }}>
                <QRCodeSVG value={magicLinkInfo.link} size={112} />
              </div>
              <div className="rounded-xl p-3 text-[12px] font-mono border max-w-md break-all"
                style={{ borderColor: BORDER, background: CARD, color: '#BBF7D0' }}>
                {magicLinkInfo.link}
              </div>
            </div>
            <p className="text-[11px] mt-2" style={{ color: GREEN }}>
              The link above also works directly, or scan it from this screen with {magicLinkInfo.childName}'s device camera.
            </p>
            <button onClick={() => setMagicLinkInfo(null)}
              className="mt-3 px-4 py-2 rounded-xl text-xs font-bold text-white" style={{ background: SAGE }}>
              Done
            </button>
          </div>
        )}

        {resetSuccessCreds && (
          <div className="rounded-2xl border-2 p-4 sm:p-5 shadow-sm"
            style={{ background: GREEN_TINT, borderColor: 'rgba(74,222,128,0.35)' }}>
            <div className="font-extrabold text-[15px] mb-1" style={{ color: GREEN }}>
              ✓ New Password Generated for {resetSuccessCreds.name}
            </div>
            <p className="text-[12.5px] mb-3" style={{ color: '#BBF7D0' }}>
              Your child can now sign in using these credentials:
            </p>
            <div className="rounded-xl p-3 text-[12.5px] font-mono border max-w-md" style={{ borderColor: BORDER, background: CARD, color: INK }}>
              <div className="font-bold mb-1">{resetSuccessCreds.name}</div>
              <div>📧 {resetSuccessCreds.email}</div>
              <div>🔑 <span className="font-black" style={{ color: GREEN }}>{resetSuccessCreds.password}</span></div>
            </div>
            <button onClick={() => setResetSuccessCreds(null)}
              className="mt-3 px-4 py-2 rounded-xl text-xs font-bold text-white" style={{ background: GREEN }}>
              Done, Saved
            </button>
          </div>
        )}

        {childCredentials && (
          <div className="rounded-2xl border-2 p-4 sm:p-5 shadow-sm"
            style={{ background: AMBER_TINT, borderColor: 'rgba(251,191,36,0.35)' }}>
            <div className="font-extrabold text-[15px] mb-1" style={{ color: AMBER }}>
              ⚠️ Save your children's login details
            </div>
            <p className="text-[12.5px] mb-3" style={{ color: '#FDE68A' }}>
              Shown once, screenshot or copy them now. You can reset them later from this dashboard.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {childCredentials.map((c, i) => (
                <div key={i} className="rounded-xl p-3 text-[12.5px] font-mono" style={{ background: CARD, color: INK }}>
                  <div className="font-bold mb-1">{c.name}</div>
                  <div>📧 {c.email}</div>
                  <div>🔑 {c.password}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setChildCredentials(null)}
              className="mt-3 px-4 py-2 rounded-xl text-xs font-bold text-white" style={{ background: SAGE }}>
              Done, I've saved them
            </button>
          </div>
        )}


        {/* ── Trial Prep Lessons Free Banner ── */}
        <div className="rounded-2xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm"
          style={{ borderColor: BORDER, background: CARD }}>
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: GREEN_TINT, color: GREEN }}>
              <Sparkles size={19} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[14.5px] font-bold" style={{ color: INK }}>
                  {t('homeschool.parent.trialPrepTitle', 'Experience 4 Free Foundation Lessons in Prep')}
                </h3>
                <span className="text-[9.5px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ background: GREEN_TINT, color: GREEN }}>
                  Free Trial
                </span>
              </div>
              <p className="text-[12.5px] mt-0.5" style={{ color: MUTED }}>
                {t('homeschool.parent.trialPrepDesc', 'Test out interactive lessons in Mathematics, Science, English, and Logic, see how Sage personalises concepts in real time.')}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/prep')}
            className="px-4 py-2.5 rounded-xl text-xs font-bold text-white flex items-center justify-center gap-1.5 whitespace-nowrap shadow-sm hover:opacity-95 transition-opacity"
            style={{ background: SAGE }}
          >
            <span>{t('homeschool.parent.tryPrepNow', 'Try Prep Lessons Now')}</span>
            <ChevronRight size={14} />
          </button>
        </div>

        {/* ── Self-study upsell ── */}
        {isPaid && !isSelfLearnActive(userProfile) && (
          <div className="rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            style={{ background: GOLD_TINT, borderColor: GOLD_BORDER }}>
            <BookOpen size={18} style={{ color: GOLD }} />
            <p className="text-[13px] flex-1" style={{ color: INK }}>
              <strong>{t('homeschool.study_yourself_too')}</strong> Add your own learning for 50% off with any active child subscription.
            </p>
            <button onClick={() => setShowSelfLearnModal(true)}
              className="px-3.5 py-2 rounded-xl text-xs font-bold text-white whitespace-nowrap" style={{ background: SAGE }}>
              {t('homeschool.parent.addMyStudies', 'Add My Studies')}
            </button>
          </div>
        )}
      </div>
              {/* ── Subscriber affiliate banner — the side-hustle door ── */}
        {isPaid && showAffiliateBanner && !(userProfile as any)?.hide_affiliate_banner && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: 'linear-gradient(135deg, #1e3a5f, #0f2744)',
              borderRadius: 18, padding: '18px 22px',
              border: '1px solid rgba(212,168,67,0.35)',
              display: 'flex', gap: 14, alignItems: 'center', position: 'relative',
            }}
          >
            <button
              onClick={async (e) => {
                e.stopPropagation()
                setShowAffiliateBanner(false)
                if (user) {
                  try {
                    const currentPrefs = (userProfile?.preferences as any) || {}
                    await supabase.from('users')
                      .update({ preferences: { ...currentPrefs, hide_affiliate_banner: true } })
                      .eq('id', user.id)
                  } catch { /* non-fatal */ }
                }
              }}
              aria-label="Dismiss"
              style={{
                position: 'absolute', top: 10, right: 10,
                background: 'rgba(255,255,255,0.1)', border: 'none',
                borderRadius: '50%', width: 22, height: 22, cursor: 'pointer',
                color: 'rgba(255,255,255,0.6)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}
            ><X size={12} /></button>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(212,168,67,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>💚</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontWeight: 800, color: '#e2e8f0', fontSize: 14.5, margin: 0 }}>
                Your subscription earns you 30% referral commission
              </p>
              <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)', margin: '3px 0 0', lineHeight: 1.5 }}>
                As an active family you're eligible for the top affiliate rate. Share your code with other homeschooling
                families, they get 5% off, you earn 30% monthly for as long as they stay subscribed.
              </p>
            </div>
            <button
              onClick={() => navigate('/affiliates/apply')}
              style={{
                padding: '9px 18px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: '#d4a843', color: '#000', fontWeight: 800, fontSize: 12.5,
                flexShrink: 0, whiteSpace: 'nowrap',
              }}
            >
              Get My Code →
            </button>
          </motion.div>
        )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 items-start">



        {/* ═══ SIDEBAR ═══ */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: BORDER, background: CARD }}>
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: BORDER }}>
            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: MUTED }}>
              {t('homeschool.parent.yourChildren', 'Your Children')}
            </span>
            <Users size={13} style={{ color: MUTED }} />
          </div>

          {children.filter(c => !c.is_demo).length === 0 ? (
            <div className="p-7 text-center">
              <GraduationCap size={26} className="mx-auto mb-2" style={{ color: MUTED }} />
              <p className="text-[13px] mb-3" style={{ color: MUTED }}>
                {t('homeschool.parent.noChildrenYet', 'No children registered yet.')}
              </p>
              <button onClick={() => navigate('/homeschool/enrol?mode=child')}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white" style={{ background: SAGE }}>
                {t('homeschool.parent.enrolChild', 'Enrol Child')}
              </button>
            </div>
          ) : (
            children.filter(c => !c.is_demo).map(child => {
              const tone = masteryTone(child.avgMastery)
              const sel = selectedChildId === child.child_id
              return (
                <button key={child.child_id} onClick={() => setSelectedChildId(child.child_id)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 border-b transition-colors"
                  style={{
                    borderColor: BORDER,
                    borderLeft: `3px solid ${sel ? SAGE : 'transparent'}`,
                    background: sel ? 'rgba(255,255,255,0.06)' : 'transparent',
                  }}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-black text-white flex-shrink-0"
                    style={{ background: SAGE }}>
                    {initials(child.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold truncate" style={{ color: INK }}>{child.name}</div>
                    <div className="text-[11px]" style={{ color: MUTED }}>
                      {child.age ? `Age ${child.age}` : '—'} · {child.totalLessons} done
                    </div>
                  </div>
                  {child.avgMastery > 0 && (
                    <span className="text-[13px] font-bold tabular-nums" style={{ color: tone.fg }}>{child.avgMastery}%</span>
                  )}
                </button>
              )
            })
          )}

          <AddChildForm currentPlan={familyPlan} onAdded={() => window.location.reload()} />

          {/* ── DEMO TILE — flips open in place ── */}
          {showDemoTile && !((userProfile as any)?.hide_demo_child) && children.some(c => c.is_demo) && (
            <div className="border-t" style={{ borderColor: BORDER, background: GOLD_TINT }}>

              <button
                onClick={() => setDemoOpen(o => !o)}
                className="w-full text-left px-4 py-3.5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-black text-[12px] flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg,#C9A84C,#A8842C)' }}>
                  {initials(children.find(c => c.is_demo)?.name || 'Ava')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13.5px] font-bold" style={{ color: INK }}>
                      {t('homeschool.parent.demoTileTitle', 'Demo')}
                    </span>
                    <Sparkles size={11} style={{ color: GOLD }} />
                  </div>
                  <div className="text-[11px]" style={{ color: MUTED }}>
                    {t('homeschool.parent.demoTileSub', 'See a sample progress report')}
                  </div>
                </div>
                <ChevronDown size={15}
                  className={`transition-transform ${demoOpen ? 'rotate-180' : ''}`}
                  style={{ color: MUTED }} />
              </button>

              <AnimatePresence>
                {demoOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                    <div className="px-4 pb-4 space-y-3">
                      <p className="text-[11.5px] leading-relaxed" style={{ color: MUTED }}>
                        {t('homeschool.parent.demoTileBlurb',
                          'A sample student so you can see exactly what reports look like before enrolling.')}
                      </p>

                      {/* Quick-glance stats */}
                      {(() => {
                        const demo = children.find(c => c.is_demo)
                        if (!demo) return null
                        return (
                          <>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                { v: `${demo.avgMastery}%`, l: t('homeschool.parent.avgMastery', 'Mastery') },
                                { v: demo.totalLessons, l: t('homeschool.parent.lessonsCompleted', 'Done') },
                                { v: demo.enrollments.length, l: t('homeschool.parent.subjectsEnrolled', 'Subjects') },
                              ].map((s, i) => (
                                <div key={i} className="rounded-xl border p-2.5 text-center" style={{ borderColor: BORDER, background: CARD }}>
                                  <div className="text-[16px] font-black tabular-nums" style={{ color: INK }}>{s.v}</div>
                                  <div className="text-[8.5px] font-bold uppercase tracking-wide" style={{ color: MUTED }}>{s.l}</div>
                                </div>
                              ))}
                            </div>

                            {/* Latest 3 reports, scannable */}
                            <div className="space-y-1.5">
                              {demo.recentLogs.filter(l => l.score !== undefined).slice(0, 3).map((l, i) => {
                                const tone = masteryToneFn(l.score || 0)
                                return (
                                  <div key={i} className="rounded-xl border px-3 py-2.5 flex items-center gap-2.5"
                                    style={{ borderColor: BORDER, background: CARD }}>
                                    <span className="text-[12.5px] font-black tabular-nums w-9"
                                      style={{ color: tone.fg }}>{l.score}%</span>
                                    <div className="flex-1 min-w-0 text-[11.5px] truncate" style={{ color: INK }}>
                                      {t('homeschool.parent.unitLessonShort', 'Unit {{u}}, Lesson {{l}}', { u: l.unit_number, l: l.lesson_number })}
                                      {(l.attempt ?? 1) > 1 && (
                                        <span className="ml-1 text-[10px] font-bold" style={{ color: GOLD }}>retry ✓</span>
                                      )}
                                    </div>
                                    <span className="text-[10px] flex-shrink-0" style={{ color: MUTED }}>{fmtDate(l.completed_at)}</span>
                                  </div>
                                )
                              })}
                            </div>

                            {/* Open the FULL report page (ChildProgressPage — module drill-down, insight, activity) */}
                            <div className="space-y-2">
                              <button
                                onClick={() => navigate(`/homeschool/child/${DEMO_CHILD_ID}`)}
                                className="w-full py-2.5 rounded-xl text-[12.5px] font-bold flex items-center justify-center gap-1.5 text-white"
                                style={{ background: 'linear-gradient(135deg,#C9A84C,#A8842C)' }}>
                                <Eye size={13} /> {t('homeschool.parent.demoOpenFull', 'Open Full Sample Report')}
                              </button>

                              <button
                                onClick={() => navigate(`/homeschool/child/${DEMO_CHILD_ID}/subjects`)}
                                className="w-full py-2 rounded-xl text-[12px] font-bold flex items-center justify-center gap-1.5 border"
                                style={{ borderColor: GOLD_BORDER, color: GOLD, background: GOLD_TINT }}>
                                <BookOpen size={13} /> {t('homeschool.parent.demoCheckSubjects', 'Check Sample Subjects')}
                              </button>
                            </div>

                            {/* Hide */}
                            <button onClick={handleHideDemo}
                              className="w-full text-[10.5px] font-semibold flex items-center justify-center gap-1 py-1"
                              style={{ color: MUTED }}>
                              <EyeOff size={10} /> {t('homeschool.parent.demoHide', 'Hide sample student')}
                            </button>
                          </>
                        )
                      })()}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* ═══ MAIN PANEL ═══ */}
        {loading ? (
          <div className="rounded-2xl border min-h-[300px] flex items-center justify-center" style={{ borderColor: BORDER, background: CARD }}>
            <Loader2 size={24} className="animate-spin text-muted-foreground" />
          </div>
        ) : activeChild ? (
          <motion.div key={activeChild.child_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="space-y-4 min-w-0">

            <ChildHero child={activeChild} />

            {/* Insight — quote card style, light, readable */}
            {activeChild.global_academic_profile?.summary && (
              <SageLearnerInsight
                profile={activeChild.global_academic_profile}
                learnerName={activeChild.name}
                accentColor={activeChild.is_demo ? GOLD : SAGE}
                cardBackground="#0F1930"
              />
            )}

            {/* Subject rows — progress-pill pattern */}
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: BORDER, background: CARD }}>
              <div className="px-5 py-3.5 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: BORDER }}>
                <h3 className="text-[15px] font-bold" style={{ color: INK }}>
                  {t('homeschool.parent.childSubjectProgress', { name: activeChild.name, defaultValue: `${activeChild.name}'s Subject Progress` })}
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/homeschool/child/${activeChild.child_id}/subjects`)}
                    className="text-[11.5px] font-bold flex items-center gap-1 hover:underline"
                    style={{ color: SAGE }}
                  >
                    <BookOpen size={13} />
                    <span>{t('homeschool.parent.checkSubjects', 'Check & Add Subjects')}</span>
                    <ChevronRight size={12} />
                  </button>
                  <button
                    onClick={() => navigate(`/homeschool/child/${activeChild.child_id}`)}
                    className="text-[11.5px] font-bold flex items-center gap-0.5 hover:underline"
                    style={{ color: MUTED }}
                  >
                    <span>{t('homeschool.parent.viewDetails', 'Full Report')}</span>
                    <ChevronRight size={12} />
                  </button>
                </div>
              </div>
              {activeChild.enrollments.length === 0 ? (
                <div className="p-10 text-center">
                  <BookOpen size={26} className="mx-auto mb-2" style={{ color: MUTED }} />
                  <p className="text-[13px] font-semibold mb-1" style={{ color: INK }}>
                    {t('homeschool.parent.noSubjectsYet', 'No subjects enrolled yet')}
                  </p>
                  <p className="text-[12.5px] mb-4" style={{ color: MUTED }}>
                    {t('homeschool.parent.addSubjectsHint', { name: activeChild.name, defaultValue: `Choose standard or Elite Academy subjects for ${activeChild.name}` })}
                  </p>
                  <button onClick={() => navigate(`/homeschool/child/${activeChild.child_id}/subjects`)}
                    className="px-4 py-2 rounded-xl text-[12.5px] font-bold text-white shadow-sm" style={{ background: SAGE }}>
                    {t('homeschool.parent.addSubjects', 'Browse & Add Subjects')}
                  </button>
                </div>
              ) : activeChild.enrollments.map((enrol, idx) => {
                const tone = masteryTone(enrol.mastery_score)
                const done = enrol.progress_state?.total_lessons_completed || 0
                return (
                  <div key={enrol.class_id}
                    className="px-5 py-3.5 flex items-center gap-3 border-b last:border-0 hover:bg-white/[0.03] transition-colors"
                    style={{ borderColor: BORDER }}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-semibold truncate" style={{ color: INK }}>{enrol.class_name}</div>
                      <div className="text-[11.5px] mt-0.5" style={{ color: MUTED }}>
                        {t('homeschool.parent.unitLesson', {
                          unit: enrol.progress_state?.current_unit || 1,
                          lesson: enrol.progress_state?.current_lesson || 1,
                          defaultValue: `Unit ${enrol.progress_state?.current_unit || 1}, Lesson ${enrol.progress_state?.current_lesson || 1}`
                        })}
                        <span className="hidden sm:inline"> · {done} done</span>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 w-32">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                        <motion.div initial={{ width: 0 }}
                          animate={{ width: `${enrol.total_lessons > 0 ? Math.min(Math.round((done / enrol.total_lessons) * 100), 100) : 0}%` }}
                          transition={{ duration: 0.7, delay: idx * 0.05 }}
                          className="h-full rounded-full" style={{ background: tone.fg }} />
                      </div>
                      <TrendChip trend={activeChild.masteryTrend} />
                    </div>
                    <span className="text-[13px] font-bold tabular-nums w-11 text-right" style={{ color: tone.fg }}>
                      {enrol.mastery_score || 0}%
                    </span>
                    {!activeChild.is_demo && (
                      <button onClick={() => navigate(`/record/${activeChild.child_id}/${enrol.class_id}`)}
                        className="text-[11px] font-bold flex items-center gap-1 px-2 py-1 rounded-lg border flex-shrink-0"
                        style={{ borderColor: 'rgba(14,165,233,0.35)', color: SAGE, background: SAGE_TINT }}>
                        <FileText size={11} /> {t('homeschool.parent.printRecord', 'Print Record')}
                      </button>
                    )}
                    {!activeChild.is_demo && enrol.mastery_score >= 90 && (
                      <button onClick={() => handleDownloadCertificate(activeChild, enrol)}
                        disabled={generatingCertFor === enrol.class_id}
                        className="text-[11px] font-bold flex items-center gap-1 px-2 py-1 rounded-lg border flex-shrink-0"
                        style={{ borderColor: GOLD_BORDER, color: GOLD, background: GOLD_TINT }}>
                        {generatingCertFor === enrol.class_id ? <Loader2 size={11} className="animate-spin" /> : <Award size={11} />}
                        {t('homeschool.parent.certificate', 'Certificate')}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Earned certificates for this child */}
            {!activeChild.is_demo && (
              <CertificateGallery userId={activeChild.child_id} compact />
            )}

            {/* Credential records — PIN management, visibility toggles, photo (parent-controlled for children) */}
            {!activeChild.is_demo && (
              <CredentialRecordsPanel
                userId={activeChild.child_id}
                isChild={true}
                publicSlug={(activeChild as any).public_slug}
              />
            )}

            {/* Activity — accordion rows, progressive disclosure */}
            {activeChild.recentLogs.length > 0 && (
              <ActivityFeed logs={activeChild.recentLogs} isDemo={!!activeChild.is_demo} />
            )}

            {/* Quick actions grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                {
                  label: t('homeschool.parent.actions.checkSubjects', 'Check Subjects'),
                  desc: t('homeschool.parent.actions.checkSubjectsDesc', 'View and adjust curriculum'),
                  icon: <BookOpen size={15} />,
                  go: () => navigate(`/homeschool/child/${activeChild.child_id}/subjects`)
                },
                {
                  label: t('homeschool.parent.actions.viewReport', 'Progress Report'),
                  desc: t('homeschool.parent.actions.viewReportDesc', 'Detailed module mastery'),
                  icon: <FileText size={15} />,
                  go: () => navigate(`/homeschool/child/${activeChild.child_id}`)
                },
                {
                  label: t('homeschool.parent.actions.trialPrep', 'Trial Prep'),
                  desc: t('homeschool.parent.actions.trialPrepDesc', 'Free foundational lessons'),
                  icon: <Sparkles size={15} />,
                  go: () => navigate('/prep')
                },
                {
                  label: t('homeschool.parent.actions.billing', 'Subscription'),
                  desc: t('homeschool.parent.actions.billingDesc', 'Manage family plan'),
                  icon: <CreditCard size={15} />,
                  go: () => navigate('/homeschool/subscription')
                },
              ].map((a) => (
                <button key={a.label} onClick={a.go}
                  className="rounded-2xl border p-3.5 text-left flex items-start gap-2.5 hover:shadow-sm transition-shadow"
                  style={{ borderColor: BORDER, background: CARD }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: SAGE_TINT, color: SAGE }}>
                    {a.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold" style={{ color: INK }}>{a.label}</div>
                    <div className="text-[11px] truncate" style={{ color: MUTED }}>{a.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        ) : (
          <div className="rounded-2xl border min-h-[220px] flex flex-col items-center justify-center text-center p-8"
            style={{ borderColor: BORDER, background: CARD }}>
            <GraduationCap size={30} className="mb-3" style={{ color: MUTED }} />
            <p className="text-sm font-semibold mb-1" style={{ color: INK }}>
              {t('homeschool.parent.selectChild', 'Select a Learner')}
            </p>
            <p className="text-[12.5px] mb-4" style={{ color: MUTED }}>
              {t('homeschool.parent.selectChildHint', 'Select a child, or open the Demo tile to preview a sample report.')}
            </p>
            {!children.some(c => c.is_demo) && (
              <button onClick={() => navigate('/homeschool/enrol?mode=child')}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white shadow-sm" style={{ background: SAGE }}>
                {t('homeschool.parent.enrolChild', 'Enrol Child')}
              </button>
            )}
          </div>
        )}
      </div>

      <AddSelfLearnModal isOpen={showSelfLearnModal} onClose={() => setShowSelfLearnModal(false)} currentPlan={familyPlan} />

      {/* ── Community library modal — parents clone free electives ── */}
      <CommunityLibraryModal
        open={communityOpen}
        onClose={() => setCommunityOpen(false)}
        canClone
        onClone={async (course, className) => {
          const res = await cloneCommunityCourse(course.id,
            { teacherId: user!.id, facilityId: userProfile!.facility_id! }, className)
          if (res.error) throw new Error(res.error)
        }}
      />
    </div>
  )

  // ── Activity feed (defined inside component for i18n access) ──
  function ActivityFeed({ logs, isDemo }: { logs: ReportRow[]; isDemo: boolean }) {
    const [openIdx, setOpenIdx] = useState<number | null>(null)
    return (
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: BORDER, background: CARD }}>
        <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: BORDER }}>
          <FileText size={14} style={{ color: SAGE }} />
          <h3 className="text-[15px] font-bold" style={{ color: INK }}>
            {t('homeschool.parent.recentReports', 'Recent Lesson & Quiz Reports')}
          </h3>
          {isDemo && (
            <span className="ml-auto text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded border"
              style={{ background: GOLD_TINT, borderColor: GOLD_BORDER, color: GOLD }}>
              {t('homeschool.parent.sampleData', 'Sample')}
            </span>
          )}
        </div>
        <div className="divide-y" style={{ borderColor: BORDER }}>
          {logs.map((log, i) => {
            const isQuiz = log.phase === 'quiz_result' || log.score !== undefined
            const retry = (log.attempt ?? 1) > 1
            const open = openIdx === i
            return (
              <div key={i} className="border-b last:border-0" style={{ borderColor: BORDER }}>
                <button onClick={() => setOpenIdx(open ? null : i)}
                  className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[12px] font-black"
                    style={isQuiz
                      ? (log.passed ? { background: GREEN_TINT, color: GREEN } : { background: ROSE_TINT, color: ROSE })
                      : { background: SAGE_TINT, color: SAGE }}>
                    {isQuiz ? (log.passed ? '✓' : '✕') : <Pencil size={12} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate" style={{ color: INK }}>
                      {log.class_name ? `${log.class_name.replace(/ \(Age 12\)| \(Master\)/g, '')} · ` : ''}
                      {t('homeschool.parent.unitLesson', {
                        unit: log.unit_number,
                        lesson: log.lesson_number,
                        defaultValue: `Unit ${log.unit_number}, Lesson ${log.lesson_number}`
                      })}
                    </div>
                    <div className="text-[11px]" style={{ color: MUTED }}>
                      {fmtDate(log.completed_at)}
                      {isQuiz && retry ? ` · ${t('homeschool.parent.retryPassed', 'retry ✓')}` : isQuiz && log.passed === false ? ` · ${t('homeschool.parent.firstAttempt', '1st attempt')}` : isQuiz ? '' : ` · ${t('homeschool.parent.lessonTaught', 'lesson')}`}
                    </div>
                  </div>
                  {isQuiz && log.score !== undefined && (
                    <span className="text-[13px] font-black tabular-nums" style={{ color: masteryToneFn(log.score).fg }}>
                      {log.score}%
                    </span>
                  )}
                  <ChevronDown size={14} className={`transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} style={{ color: MUTED }} />
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                      transition={{ duration: 0.22 }} className="overflow-hidden">
                      <div className="px-5 pb-4 pl-16 space-y-2.5">
                        {!isQuiz && log.concept_understood && (
                          <p className="text-[12.5px] italic leading-relaxed" style={{ color: MUTED }}>
                            “{log.concept_understood}”
                          </p>
                        )}
                        {log.weak_concept_flagged && (
                          <p className="text-[12px]" style={{ color: ROSE }}>
                            {t('homeschool.parent.flagged', 'Flagged')}: {log.weak_concept_flagged}
                          </p>
                        )}
                        {((log.strong_areas?.length ?? 0) > 0 || (log.weak_areas?.length ?? 0) > 0) && (
                          <div className="flex flex-wrap gap-1.5">
                            {log.strong_areas?.map((s, si) => (
                              <span key={`s${si}`} className="text-[11px] font-medium px-2 py-0.5 rounded-md border"
                                style={{ background: GREEN_TINT, borderColor: GREEN_TINT, color: GREEN }}>✓ {s}</span>
                            ))}
                            {log.weak_areas?.map((w, wi) => (
                              <span key={`w${wi}`} className="text-[11px] font-medium px-2 py-0.5 rounded-md border"
                                style={{ background: ROSE_TINT, borderColor: ROSE_TINT, color: ROSE }}>
                                {t('homeschool.parent.review', 'Review')}: {w}
                              </span>
                            ))}
                          </div>
                        )}
                        {isDemo && (
                          <p className="text-[10.5px] italic pt-1 border-t" style={{ color: MUTED, borderColor: BORDER }}>
                            {t('homeschool.parent.demoFootnote', 'Sample student, real curriculum, representative results.')}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// AddChildForm — billed add via add-family-learner
// ═════════════════════════════════════════════════════════════════════════════
function AddChildForm({ currentPlan, onAdded }: { currentPlan: string; onAdded: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [age, setAge] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [created, setCreated] = useState<{ name: string; email: string; password: string } | null>(null)

  const addonPrice = currentPlan === 'edu_elite' ? '$22.50' : currentPlan === 'edu_bundle' ? '$29.50' : '$17.50'

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 14,
    border: '1px solid #BORDER', outline: 'none', background: CANVAS, color: INK, boxSizing: 'border-box',
  }

  if (!currentPlan) {
    return (
      <button onClick={() => navigate('/homeschool/subscription')}
        className="w-full px-4 py-3.5 border-t text-sm transition-colors flex items-center justify-center gap-2"
        style={{ borderColor: BORDER, color: MUTED }}
        onMouseEnter={e => (e.currentTarget.style.color = SAGE)}>
        <Plus size={14} /> {t('homeschool.parent.subscribeToAdd', 'Subscribe to add another learner')}
      </button>
    )
  }

  async function handleAdd() {
    if (!name || !age) { setError(t('homeschool.parent.addChild.errorRequired', 'Name and age are required.')); return }
    setSaving(true); setError('')
    try {
      const { data, error: fnError } = await supabase.functions.invoke('add-family-learner', {
        body: { is_self: false, learner_name: name, age: parseInt(age, 10), plan_key: currentPlan },
      })
      if (fnError) throw new Error(fnError.message)
      if (data?.error) throw new Error(data.error)
      setCreated({ name, email: data.learner?.email ?? '', password: data.learner?.password ?? '' })
      setOpen(false); setName(''); setAge('')
    } catch (err: any) {
      setError(err.message || t('homeschool.parent.addChild.errorGeneric', 'Failed to add child learner.'))
    } finally { setSaving(false) }
  }

  if (created) {
    return (
      <div className="p-4 border-t space-y-2.5" style={{ borderColor: BORDER }}>
        <div className="text-[11.5px] font-bold rounded-lg p-2.5 border"
          style={{ background: GOLD_TINT, borderColor: GOLD_BORDER, color: GOLD }}>
          {t('homeschool.parent.saveCredentials', 'Save login details, shown once')}
        </div>
        <div className="rounded-lg p-3 text-[11.5px] font-mono space-y-1" style={{ background: CARD, color: INK }}>
          <div>📧 {created.email}</div>
          <div>🔑 {created.password}</div>
        </div>
        <p className="text-[10.5px] leading-relaxed" style={{ color: MUTED }}>
          {t('homeschool.parent.addedAtRate', 'Added at the 50% rate ({{price}}/mo). Access starts now; billing at your next renewal.', { price: addonPrice })}
        </p>
        <button
          onClick={() => { setCreated(null); onAdded(); }}
          className="w-full text-white py-2 rounded-xl text-xs font-bold shadow-sm"
          style={{ background: SAGE }}
        >
          {t('common.done', 'Done')}
        </button>
      </div>
    )
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="w-full px-4 py-3.5 border-t text-sm transition-colors flex items-center justify-center gap-2"
        style={{ borderColor: BORDER, color: MUTED }}
        onMouseEnter={e => (e.currentTarget.style.color = SAGE)}>
        <Plus size={14} /> {t('homeschool.parent.addAnotherChild', 'Add another child: 50% off ({{price}}/mo)', { price: addonPrice })}
      </button>
    )
  }

  return (
    <div className="p-4 border-t space-y-2.5" style={{ borderColor: BORDER }}>
      <div className="font-bold text-xs" style={{ color: INK }}>
        {t('homeschool.parent.addChildTitle', 'Add a child')} <span className="font-medium" style={{ color: MUTED }}>— {addonPrice}/mo</span>
      </div>
      <input
        placeholder={t('homeschool.parent.addChild.namePlaceholder', 'Child name')}
        value={name}
        onChange={e => setName(e.target.value)}
        style={inputStyle}
      />
      <input
        placeholder={t('homeschool.parent.addChild.agePlaceholder', 'Age')}
        type="number"
        value={age}
        onChange={e => setAge(e.target.value)}
        style={inputStyle}
      />
      <p className="text-[11px]" style={{ color: MUTED }}>
        {t('homeschool.parent.addChild.credentialNote', "Login details are generated automatically, you'll see them after adding.")}
      </p>
      {error && <div className="text-xs text-red-600">{error}</div>}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleAdd}
          disabled={saving}
          className="flex-1 py-2 rounded-xl text-xs font-bold text-white shadow-sm disabled:opacity-50"
          style={{ background: SAGE }}
        >
          {saving ? t('homeschool.parent.addChild.adding', 'Adding...') : t('homeschool.parent.addChild.submit', 'Add & Bill at 50%')}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="px-3.5 py-2 border rounded-xl text-xs"
          style={{ borderColor: BORDER, color: MUTED }}
        >
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  )
}
